import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, Flame, FolderKanban, Layers, ListChecks, Milestone } from 'lucide-react';
import { prmInsightsApi, PrmInsights } from '../../../services/projectService';
import { PrmInsightsSkeleton } from '../../../components/common/InsightsSkeleton';
import { usePermission } from '../../../contexts/PermissionContext';
import NoDataFound from '../../../components/common/NoDataFound';

const EMPTY: PrmInsights = {
    overdue_tasks: 0, due_week: 0, due_month: 0, critical_pending: 0,
    open_tasks: 0, total_tasks: 0, completion_rate: 0, active_projects: 0,
    projects_by_status: [], tasks_by_status: [], tasks_by_priority: [],
    per_project: [], spotlight: [],
};

const STATUS_COLORS = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f97316', '#94a3b8'];
const PRIORITY_COLOR: Record<string, string> = { CRITICAL: '#ef4444', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: '#10b981', Unset: '#94a3b8' };
const PRIORITY_CHIP: Record<string, string> = {
    CRITICAL: 'bg-red-50 text-red-700 border-red-200', HIGH: 'bg-orange-50 text-orange-700 border-orange-200',
    MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200', LOW: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const FMT_DATE = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: any) => (
    <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
        style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
        {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
    </div>
);

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1', className = '' }: {
    title: string; data: { label: string; count: number; color: string }[];
    icon: any; total: number; accentColor?: string; className?: string;
}) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    return (
        <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 italic">No data yet.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1">
                    {data.map((item, idx) => {
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">
                                    {item.label.toLowerCase().replace(/_/g, ' ')}
                                </span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${item.color}bb, ${item.color})`, boxShadow: barPct > 10 ? `0 1px 4px ${item.color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? item.color : '#94a3b8' }}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const InsightsTab: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission('PRM_PROJECTS', 'view');
    const [data, setData] = useState<PrmInsights>(EMPTY);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoading(true);
        prmInsightsApi.get()
            .then(d => { if (!cancelled && d) setData(d); })
            .catch(() => { /* swallow load errors */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [canView]);

    const taskStatusData = useMemo(() => data.tasks_by_status.map((r, i) => ({ label: r.label, count: r.count, color: STATUS_COLORS[i % STATUS_COLORS.length] })), [data.tasks_by_status]);
    const taskPriorityData = useMemo(() => data.tasks_by_priority.map(r => ({ label: r.label, count: r.count, color: PRIORITY_COLOR[r.label] || '#94a3b8' })), [data.tasks_by_priority]);
    const projectStatusData = useMemo(() => data.projects_by_status.map((r, i) => ({ label: r.label, count: r.count, color: STATUS_COLORS[i % STATUS_COLORS.length] })), [data.projects_by_status]);
    const taskStatusTotal = useMemo(() => taskStatusData.reduce((s, r) => s + r.count, 0), [taskStatusData]);
    const taskPriorityTotal = useMemo(() => taskPriorityData.reduce((s, r) => s + r.count, 0), [taskPriorityData]);
    const projectStatusTotal = useMemo(() => projectStatusData.reduce((s, r) => s + r.count, 0), [projectStatusData]);
    const perProjectMax = useMemo(() => Math.max(...data.per_project.map(p => p.open_count), 1), [data.per_project]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to Project Management insights." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <PrmInsightsSkeleton />}
                    {!loading && (
                        <div className="py-4 space-y-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Overdue Tasks" value={data.overdue_tasks} sub="Past due date" icon={AlertTriangle} iconBg="#fef2f2" iconColor="#ef4444" />
                                <KpiCard label="Due This Week" value={data.due_week} sub={`${data.due_month} due in 30 days`} icon={CalendarClock} iconBg="#fffbeb" iconColor="#f59e0b" />
                                <KpiCard label="Critical Pending" value={data.critical_pending} sub="Critical & not done" icon={Flame} iconBg="#fff7ed" iconColor="#f97316" />
                                <KpiCard label="Active Projects" value={data.active_projects} sub={`${data.completion_rate}% tasks complete`} icon={FolderKanban} iconBg="#ecfdf5" iconColor="#10b981" />
                            </div>

                            {(data.overdue_tasks > 0 || data.critical_pending > 0) && (
                                <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-xl flex gap-2 items-center" style={{ boxShadow: '0 1px 4px rgba(239,68,68,0.08)' }}>
                                    <AlertTriangle size={13} className="text-red-500 shrink-0" />
                                    <p className="text-xs text-red-700">
                                        <span className="font-semibold">{data.overdue_tasks}</span> task{data.overdue_tasks === 1 ? '' : 's'} overdue
                                        {data.critical_pending > 0 && <> · <span className="font-semibold">{data.critical_pending}</span> critical task{data.critical_pending === 1 ? '' : 's'} still pending</>}.
                                    </p>
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-4">
                                <DistributionWidget title="Tasks by Status" data={taskStatusData} icon={ListChecks} total={taskStatusTotal} accentColor="#6366f1" />
                                <DistributionWidget title="Tasks by Priority" data={taskPriorityData} icon={Flame} total={taskPriorityTotal} accentColor="#ef4444" />
                                <DistributionWidget title="Projects by Status" data={projectStatusData} icon={Layers} total={projectStatusTotal} accentColor="#3b82f6" />
                            </div>

                            {/* Spotlight table */}
                            <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white">
                                    <div className="p-1.5 rounded-lg" style={{ background: '#ef444412' }}><AlertTriangle size={12} style={{ color: '#ef4444' }} /></div>
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Pending Critical &amp; Overdue Tasks</span>
                                </div>
                                {data.spotlight.length === 0 ? (
                                    <p className="text-xs text-slate-400 text-center py-8 italic">No overdue or critical pending tasks.</p>
                                ) : (
                                    <div className="table-list"><div className="table-list-scroll">
                                        <table className="table-list-table" data-testid="TBL_prm_insights_spotlight">
                                            <thead><tr>
                                                <th className="table-list-th">Task</th>
                                                <th className="table-list-th">Project</th>
                                                <th className="table-list-th">Priority</th>
                                                <th className="table-list-th">Due</th>
                                                <th className="table-list-th">Status</th>
                                            </tr></thead>
                                            <tbody>
                                                {data.spotlight.map((t, i) => (
                                                    <tr key={`${t.project_name}-${t.task_name}-${i}`} className="table-list-row">
                                                        <td className="table-list-cell"><span className="table-list-cell-title flex items-center gap-1">{t.is_milestone && <Milestone size={12} className="text-indigo-500 shrink-0" />}{t.task_name}</span></td>
                                                        <td className="table-list-cell">{t.project_name}</td>
                                                        <td className="table-list-cell">{t.priority ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${PRIORITY_CHIP[t.priority] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{t.priority}</span> : <span className="text-slate-400">—</span>}</td>
                                                        <td className="table-list-cell"><span className={t.is_overdue ? 'text-red-600 font-semibold' : ''}>{FMT_DATE(t.due_date)}</span></td>
                                                        <td className="table-list-cell">{t.status.replace(/_/g, ' ')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div></div>
                                )}
                            </div>

                            {/* Open tasks per project */}
                            <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white">
                                    <div className="p-1.5 rounded-lg" style={{ background: '#6366f112' }}><FolderKanban size={12} style={{ color: '#6366f1' }} /></div>
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Open Tasks by Project</span>
                                </div>
                                {data.per_project.length === 0 ? (
                                    <p className="text-xs text-slate-400 text-center py-8 italic">No open project tasks.</p>
                                ) : (
                                    <ul className="divide-y divide-slate-50">
                                        {data.per_project.map((p, i) => {
                                            const barPct = Math.round((p.open_count / perProjectMax) * 100);
                                            const color = STATUS_COLORS[i % STATUS_COLORS.length];
                                            return (
                                                <li key={p.label} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                                    style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                                    <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate flex items-center gap-2">
                                                        {p.label}
                                                        {p.overdue_count > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100 shrink-0">{p.overdue_count} overdue</span>}
                                                    </span>
                                                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                                        <div className="h-1.5 rounded-full transition-all duration-500"
                                                            style={{ width: barPct > 0 ? `${barPct}%` : '4%', background: `linear-gradient(90deg, ${color}bb, ${color})`, boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none' }} />
                                                    </div>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <span className="text-[12px] font-extrabold tabular-nums" style={{ color }}>{p.open_count}</span>
                                                        <span className="text-[10px] text-slate-400">open</span>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InsightsTab;
export { InsightsTab };
